import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Add, Delete, Search } from '@mui/icons-material';
import { loadGlobalPayload, saveGlobalPayload, collectAllKeysFromSuites } from '../utils/globalPayload';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (globalPayload: Record<string, string>, opts?: { addMissing?: boolean }) => void;
  suites: any[]; // readonly view used for preview counts
  isDarkMode?: boolean;
}

const GlobalPayloadConfig: React.FC<Props> = ({ open, onClose, onApply, suites, isDarkMode = false }) => {
  const theme = {
    bgSecondary: isDarkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    bgInput: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    textPrimary: isDarkMode ? '#ffffff' : '#1e293b',
    textSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
    textMuted: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    borderFocus: isDarkMode ? 'rgba(124, 58, 237, 0.6)' : 'rgba(124, 58, 237, 0.8)',
  };

  const textFieldSx = {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.bgInput,
      color: theme.textPrimary,
      borderColor: theme.border,
      '& fieldset': { borderColor: theme.border },
      '&:hover fieldset': { borderColor: theme.textMuted },
      '&.Mui-focused fieldset': { borderColor: theme.borderFocus, borderWidth: '1px' },
      '& input': { color: theme.textPrimary },
      '& input::placeholder': { color: theme.textMuted, opacity: 1 },
    },
  };
  const [entries, setEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [addMissing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const saved = loadGlobalPayload();
    const fromSuites = collectAllKeysFromSuites(suites || []);
    const allKeys = Array.from(
      new Set([...Object.keys(saved), ...Object.keys(fromSuites)])
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const arr = allKeys.map((k) => ({
      key: k,
      value: saved.hasOwnProperty(k) ? String(saved[k]) : String(fromSuites[k] ?? ''),
    }));
    setEntries(arr.length ? arr : [{ key: '', value: '' }]);
    setSearchQuery('');
  }, [open, suites]);

  const searchLower = searchQuery.trim().toLowerCase();
  const entryMatchesSearch = (e: { key: string; value: string }) =>
    !searchLower ||
    e.key.toLowerCase().includes(searchLower) ||
    e.value.toLowerCase().includes(searchLower);

  const setEntry = (idx: number, field: 'key' | 'value', val: string) => {
    setEntries((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  };

  const addRow = () => setEntries((p) => [...p, { key: '', value: '' }]);
  const deleteRow = (idx: number) => setEntries((p) => p.filter((_, i) => i !== idx));

  const toPayload = () =>
    entries.reduce<Record<string, string>>((acc, e) => {
      if (e.key && e.key.trim() !== '') acc[e.key.trim()] = e.value;
      return acc;
    }, {});

  const handleSave = () => {
    const payload = toPayload();
    saveGlobalPayload(payload);
    onApply(payload, { addMissing });
    onClose();
  };

  const previewCount = () => {
    const payload = toPayload();
    if (!suites || Object.keys(payload).length === 0) return 0;
    let count = 0;
    suites.forEach((suite) =>
      suite.scenarios?.forEach((sc: any) => {
        const body = sc.payload || sc.apiRequest || sc.apiRequest?.body || {};
        try {
          const obj = typeof body === 'string' ? JSON.parse(body) : body || {};
          Object.keys(payload).forEach((k) => {
            if (obj && Object.prototype.hasOwnProperty.call(obj, k)) count += 1;
          });
        } catch {
          // ignore malformed JSON for preview
        }
      })
    );
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: theme.bgSecondary,
          color: theme.textPrimary,
          border: `1px solid ${theme.border}`,
        },
      }}
    >
      <DialogTitle sx={{ color: theme.textPrimary, overflow: 'visible', wordBreak: 'break-word' }}>
        Global Payload Configuration
      </DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        <Typography variant="body2" sx={{ mb: 1, color: theme.textSecondary, overflow: 'visible', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
          Define key–value pairs that will be propagated to all test-case request payloads that contain the key.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search keys or values..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2, ...textFieldSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.textMuted, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Divider sx={{ mb: 2, borderColor: theme.border }} />
        {entries.map((e, idx) =>
          !entryMatchesSearch(e) ? null : (
          <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, minWidth: 0 }}>
            <TextField
              placeholder="Key"
              value={e.key}
              onChange={(ev) => setEntry(idx, 'key', ev.target.value)}
              sx={{ ...textFieldSx, flex: 1 }}
              size="small"
            />
            <TextField
              placeholder="Value"
              value={e.value}
              onChange={(ev) => setEntry(idx, 'value', ev.target.value)}
              sx={{ ...textFieldSx, flex: 2 }}
              size="small"
            />
            <IconButton onClick={() => deleteRow(idx)} size="small" aria-label="delete" sx={{ flexShrink: 0, color: theme.textSecondary }} color="inherit">
              <Delete />
            </IconButton>
          </Box>
        )
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>
          <Button startIcon={<Add />} onClick={addRow}>
            Add
          </Button>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Typography variant="caption" sx={{ color: theme.textSecondary, alignSelf: 'center', overflow: 'visible', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
            Preview matches: {previewCount()} (counts total matching keys across scenarios)
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.border}` }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save and Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalPayloadConfig;

